import {
  Component,
  HostBinding,
  OnInit,
  OnDestroy,
  ElementRef,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef
} from '@angular/core';
import { AdaptiveService } from '@uxdf/direct-shared-lib';
import { Subscription } from 'rxjs';
import { startWith, filter, take, map } from 'rxjs/operators';
import { internalTilesConfig } from './config';
import { State, __config_layout } from '../../../redux/reducers';
import { Store, select } from '@ngrx/store';
import { TileViewData, AdaptiveTileMap, ITileConfig } from '../../../redux/models/tiles.model';

@Component({
  standalone: false,
  selector: 'app-homepage-tiles',
  templateUrl: './tiles.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TilesComponent implements OnInit, AfterViewInit, OnDestroy {
  @HostBinding() class = 'homepage-tiles';
  isMobile: boolean;
  isExtraSmall: boolean;
  adaptiveTypeMap: AdaptiveTileMap;
  // array of rows which are arrays of tiles
  layout: TileViewData[][];
  backendConfig: ITileConfig[];
  private readonly subscriptions: Subscription;
  // private modularConfigSubscription: Subscription;
  private containerWidth: number;

  constructor(
    private readonly adaptiveService: AdaptiveService,
    private readonly element: ElementRef,
    private readonly store: Store<State>,
    private readonly changeDetector: ChangeDetectorRef
  ) {
    this.subscriptions = new Subscription();
  }

  ngOnInit() {
    // screen resize handler
    this.subscriptions.add(
      this.adaptiveService
        .getResizeEvent()
        .pipe(startWith(null))
        .subscribe(() => {
          this.isMobile = this.adaptiveService.isMobile();
          this.isExtraSmall = this.adaptiveService.isExtraSmall();
          this.containerWidth = this.element.nativeElement.clientWidth;
          this.updateAdaptiveTypeMap();
        })
    );
    // get config
    this.store
      .pipe(
        select(__config_layout),
        filter(configState => configState.config && !configState.isLoading),
        map(configState => configState.config.home_page_tiles),
        take(1)
      )
      .subscribe(config => {
        this.backendConfig = config;
        this.makeLayoutFromConfig();
        this.changeDetector.detectChanges();
      });
  }

  setRowType(layout) {
    for (const r of layout) {
      for (const t of r) {
        t.rowType = r.length > 1 ? 'multiple' : 'single';
      }
    }
    return layout;
  }

  ngAfterViewInit() {
    // changes input values for child components, should be async
    Promise.resolve().then(() => {
      this.containerWidth = this.element.nativeElement.clientWidth;
      this.updateAdaptiveTypeMap();
    });
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  rowTrackBy = (index: number, row: TileViewData[]) =>
    row.reduce((acc, tile) => acc + this.tileTrackBy(null, tile), '');

  tileTrackBy(index: number, tile: TileViewData): string {
    return tile.module + tile.size;
  }

  /**
   * merge Magento config with our data and split into layout rows
   */
  private makeLayoutFromConfig() {
    let layout = [];
    let row = [];
    let rowFill = 0;
    for (const c of this.backendConfig) {
      row.push(internalTilesConfig[c.module] ? { ...c, ...internalTilesConfig[c.module] } : c);
      rowFill += c.size === 'large' ? 3 : c.size === 'medium' ? 2 : 1;
      if (3 === rowFill) {
        layout.push(row);
        row = [];
        rowFill = 0;
      }
    }
    if (row.length) {
      layout.push(row);
    }

    layout = this.setRowType(layout);
    this.layout = layout;
  }

  /**
   * calculate conditional tile sizes to update tile types in template
   */
  private updateAdaptiveTypeMap() {
    // all tiles has same size on certain resolutions
    const forcedType = this.isExtraSmall ? 's' : this.isMobile ? 'm' : null;
    this.adaptiveTypeMap = {
      // one tile in a row
      single: {
        small: forcedType || 's',
        medium: forcedType || 'm',
        large: forcedType || 'l'
      },
      // two tiles in a row
      multiple: {
        small: forcedType || 's',
        // if there's no room for non-small tiles
        medium: forcedType || (this.containerWidth < 770 ? 's' : 'm'),
        large: forcedType || (this.containerWidth < 770 ? 's' : 'l')
      }
    };
    this.changeDetector.detectChanges();
   }
 get filteredLayout() {
  return (this.layout || []).map(row => row.filter(tile => tile.module !== 'sso'));
  }
}
